import { Injectable } from '@angular/core';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { BillingApiData } from 'src/app/pages/new-billing/types/api.types';
import { AddonIds, AddonPricingService } from 'src/app/services/addon-pricing/addon-pricing.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { AuthCompany } from 'src/models';

/**
 * Shared utilities to read GrowthBook add-on metadata and evaluate self-serve purchase state.
 */
export interface SelfServeAddonLike {
  selfServeAddon?: boolean;
  activationIn?: string | null;
  addonIds?: Partial<AddonIds> | null;
}

export interface UpsellVisibilityState {
  showUpsell: boolean;
}

export interface ComputeShowUpsellParams {
  addonIds: Partial<AddonIds> | null | undefined;
  addonConfig: SelfServeAddonLike | null | undefined;
  flagEnabled: boolean;
  company: Pick<AuthCompany, 'userCount' | 'subscription'> | null | undefined;
  billingData: any;
  /** Optional legacy subscription key substrings to detect addon presence when IDs are unavailable. */
  legacySubscriptionKeys?: string[];
  /** Optional extra billing plan IDs (see UpsellConfig.additionalBillingPlanIdsForAddonMatch). */
  extraBillingPlanIdCandidates?: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class SelfServeActivationService {
  constructor(
    private readonly addonPricingService: AddonPricingService,
    private readonly growthbook: GrowthBookService,
  ) {}

  /** Normalize GrowthBook keys to our AddonIds shape. */
  public normalizeAddonIds(obj: Record<string, string> | null | undefined): AddonIds {
    const get = (...keys: string[]) => keys.map(k => (obj as any)?.[k]).find(Boolean) || '';
    return {
      monthly: get('monthly', 'month'),
      yearly: get('yearly', 'year', 'annual', 'annually'),
      quarterly: get('quarterly', 'quarter'),
      semi_yearly: get('semi_yearly', 'semi-year', 'semi_year', 'semiannual', 'semi-annual'),
    } as AddonIds;
  }

  /**
   * Read an add-on config + normalized ids from GrowthBook by normalized linkname.
   * Used when the exact GrowthBook linkname is unknown but the normalized form is.
   * Normalization strips non-alphanumeric characters and lowercases (e.g.
   * "Software Cost Insights" → "softwarecostinsights").
   */
  public readAddonFromGBNormalized(normalizedLinkname: string): { config: SelfServeAddonLike | undefined, ids: AddonIds } {
    const emptyIds: AddonIds = { monthly: '', yearly: '', quarterly: '', semi_yearly: '' } as AddonIds;
    try {
      const normalize = (s: any) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const addonsResponse = this.growthbook.getFeatureValue('addons-list', { flowId: '', addons: [] as any[] });
      const addon = addonsResponse?.addons?.find(
        (a: any) => normalize(a?.linkname ?? a?.linkName) === normalizedLinkname,
      ) as SelfServeAddonLike | undefined;

      const ids: AddonIds = addon?.addonIds
        ? this.normalizeAddonIds(addon.addonIds as Record<string, string>)
        : emptyIds;
      return { config: addon, ids };
    } catch {
      return { config: undefined, ids: emptyIds };
    }
  }

  /**
   * Read an add-on config + normalized ids from GrowthBook safely by linkname (exact match).
   */
  public readAddonFromGB(linkname: string): { config: SelfServeAddonLike | undefined, ids: AddonIds } {
    try {
      const addonsResponse = this.growthbook.getFeatureValue('addons-list', { flowId: '', addons: [] as any[] });
      const addon = addonsResponse?.addons?.find(
        (a: any) => a?.linkname === linkname || a?.linkName === linkname,
      ) as SelfServeAddonLike | undefined;

      const ids: AddonIds = addon?.addonIds
        ? this.normalizeAddonIds(addon.addonIds as Record<string, string>)
        : { monthly: '', yearly: '', quarterly: '', semi_yearly: '' } as AddonIds;
      return { config: addon, ids };
    } catch {
      return { config: undefined, ids: { monthly: '', yearly: '', quarterly: '', semi_yearly: '' } as AddonIds };
    }
  }

  /**
   * Generalized check whether an add-on is purchased, using billing data when available
   * and falling back to company.subscription.addOns (supports legacy keys).
   */
  public hasPurchasedAddon(
    company: Pick<AuthCompany, 'userCount' | 'subscription'> | null | undefined,
    billingData: BillingApiData,
    addonIds: Partial<AddonIds> | null | undefined,
    legacySubscriptionKeys?: string[],
    extraBillingPlanIdCandidates?: readonly string[],
  ): boolean {
    const fromAddon = Object.values(addonIds || {}).filter((v): v is string => Boolean(v));
    const addonIdValues = [...fromAddon, ...(extraBillingPlanIdCandidates || [])];
    // Prefer billing data
    if (billingData) {
      try {
        const userCount = billingData?.billingDetails?.quantity ?? company?.userCount ?? 1;
        const discountRemoval = this.growthbook.getFeatureValue('billing:discount_removal_companies', false);
        const currentPlan = new ConcretePlan(billingData, userCount, discountRemoval);
        const subscriptionPlanIds = this.addonPricingService.getSubscriptionPlanIds(currentPlan);

        const purchasedByIds = addonIdValues.length > 0
          && subscriptionPlanIds.some(id => addonIdValues.includes(id));
        if (purchasedByIds) return true;

        // Fallback to legacy/company addOns if not detected via plan ids
        const subs = (company?.subscription?.addOns || []).map((v: unknown) => String(v));
        const legacyKeysArr = Array.isArray(legacySubscriptionKeys) && legacySubscriptionKeys.length > 0
          ? legacySubscriptionKeys
          : [];
        const hasLegacyMatch = legacyKeysArr.length > 0 && subs.some(v => legacyKeysArr.some(k => v.includes(k)));
        const hasIdsMatch = addonIdValues.length > 0 && subs.some(v => addonIdValues.includes(v));
        return hasLegacyMatch || hasIdsMatch;
      } catch {
        // fall through to company fallback
      }
    }

    // No billing data (or error) → rely on subscription.addOns and GB IDs
    const sub = (company?.subscription?.addOns || []).map((v: any) => String(v));
    const legacyKeys = Array.isArray(legacySubscriptionKeys) && legacySubscriptionKeys.length > 0
      ? legacySubscriptionKeys
      : [];
    const hasLegacy = legacyKeys.length > 0 && sub.some(v => legacyKeys.some(k => v.includes(k)));
    const hasIds = addonIdValues.length > 0 && sub.some(v => addonIdValues.includes(v));
    return hasLegacy || hasIds;
  }

  /** Convenience: check UAR purchase combining billing data and legacy keys. */
  public hasPurchasedUAR(
    company: Pick<AuthCompany, 'userCount' | 'subscription'> | null | undefined,
    billingData: BillingApiData,
  ): boolean {
    const { ids } = this.readAddonFromGB('UAR');
    return this.hasPurchasedAddon(company, billingData, ids, ['uar_add_on']);
  }

  /**
   * Result structure for computing an upsell card's visibility.
   */
  public static readonly defaultUpsellState = Object.freeze({
    showUpsell: false,
  });

  public computeShowUpsellState(params: ComputeShowUpsellParams): UpsellVisibilityState {
    try {
      const { addonIds, flagEnabled, company, billingData, legacySubscriptionKeys, extraBillingPlanIdCandidates } = params || {} as any;

      // Determine purchase state via shared helper
      const hasAddon = this.hasPurchasedAddon(
        company,
        billingData,
        addonIds,
        legacySubscriptionKeys?.length ? legacySubscriptionKeys : [],
        extraBillingPlanIdCandidates,
      );

      const show = Boolean(flagEnabled) && !hasAddon;
      return { showUpsell: show };
    } catch {
      return { ...SelfServeActivationService.defaultUpsellState };
    }
  }
}
